'use client'

import BidIcon from '@/icons/bid'

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Link from 'next/link'
import classNames from 'classnames'
import { motion, type MotionStyle } from 'framer-motion'
import { ArrowRight } from 'lucide-react'
import BitcoinIcon from '@/icons/bitcoin-filled'
import CollectionIcon from '@/components/collection-icon'
import MakeOfferModal from '@/components/offers/make-offer-modal'
import ErrorBoundary from '@/components/error-boundary'
import { COLLECTION_RAIL_WIDTH } from '@/utils/collection-layout'
import useIsHydrated from '@/utils/hooks/useIsHydrated'
import useExchangeRate from '@/utils/hooks/useExchangeRate'
import { offersEnabled } from '@/utils/offers'
import type { RealBidBook } from '@/utils/bid-book-real'
import {
	floorTint,
	formatBtcTotal,
	type BidCollection,
	type BidListing
} from '@/utils/bids'
import { CollectionBidsTreemap, InscriptionBidsTreemap } from './treemaps'
import { BidActivityRail, TopBidsRail } from './rails'
import BidAmount from './bid-amount'
import useBidBook from './use-bid-book'
import styles from './bids.module.css'

// The entrance cascade runs once; after this every change is a pulse.
const ENTER_MS = 1_400

function useElementWidth<T extends HTMLElement>() {
	const [width, setWidth] = useState(0)
	const observer = useRef<ResizeObserver | null>(null)
	// Callback ref: the measured node moves between the desktop and phone layouts.
	const ref = useCallback((el: T | null) => {
		observer.current?.disconnect()
		observer.current = null
		if (!el) return
		const update = () => setWidth(Math.floor(el.getBoundingClientRect().width))
		update()
		observer.current = new ResizeObserver(update)
		observer.current.observe(el)
	}, [])
	return [ref, width] as const
}

const desktopQuery = '(min-width: 1024px)'
function useIsDesktop() {
	const [desktop, setDesktop] = useState(true)
	useEffect(() => {
		const q = window.matchMedia(desktopQuery)
		const update = () => setDesktop(q.matches)
		update()
		q.addEventListener('change', update)
		return () => q.removeEventListener('change', update)
	}, [])
	return desktop
}

/** True for the first moment after mount, while the board settles in. */
function useEntering() {
	const [entering, setEntering] = useState(true)
	useEffect(() => {
		const t = setTimeout(() => setEntering(false), ENTER_MS)
		return () => clearTimeout(t)
	}, [])
	return entering
}

/** The board's shape while the first read is in flight. */
function BoardSkeleton() {
	const blocks = [
		'col-span-2 row-span-2',
		'col-span-2',
		'',
		'',
		'col-span-2',
		'',
		'',
		'',
		''
	]
	return (
		<div
			role="status"
			aria-label="Loading offers"
			className="grid h-[360px] grid-cols-4 grid-rows-4 gap-[3px] animate-pulse motion-reduce:animate-none"
		>
			{blocks.map((span, i) => (
				<div key={i} className={classNames('rounded-lg bg-muted', span)} />
			))}
		</div>
	)
}

export default function BidsView({
	collections,
	listings,
	book
}: {
	collections: BidCollection[]
	listings: Record<string, BidListing[]>
	book: RealBidBook | null
}) {
	const hydrated = useIsHydrated()
	const desktop = useIsDesktop()
	const entering = useEntering()
	const biddingOpen = hydrated && offersEnabled()
	const { exchangeRate } = useExchangeRate()

	const {
		collectionBids: shownCollectionBids,
		groups: shownGroups,
		activity,
		pulses,
		status,
		addActivity,
		retry
	} = useBidBook({ collections, book, realOffers: biddingOpen })

	// --- Totals
	const collectionTotal = shownCollectionBids.reduce((s, b) => s + b.priceSats * b.quantity, 0)
	const inscriptionTotal = shownGroups.reduce((s, g) => s + g.totalSats, 0)
	const inscriptionCount = shownGroups.reduce((n, g) => n + g.bids.length, 0)
	const liveBids = shownCollectionBids.length + inscriptionCount
	const topBid = shownCollectionBids[0]
	const bidders = useMemo(
		() =>
			new Set([
				...shownCollectionBids.map((b) => b.bidder),
				...shownGroups.flatMap((g) => g.bids.map((b) => b.bidder))
			]).size,
		[shownCollectionBids, shownGroups]
	)
	const empty = status === 'ready' && liveBids === 0

	// --- Selection + bid modal
	const [selectedSlug, setSelectedSlug] = useState<string | null>(null)
	const [bidSlug, setBidSlug] = useState<string | null>(null)
	const bidCollection = collections.find((c) => c.slug === bidSlug)
	const bidTop = shownCollectionBids.find((b) => b.slug === bidSlug)

	const rootRef = useRef<HTMLDivElement | null>(null)
	const gridRef = useRef<HTMLDivElement | null>(null)
	const [centerRef, centerWidth] = useElementWidth<HTMLDivElement>()
	const [mobileView, setMobileView] = useState<'bids' | 'top' | 'activity'>('bids')

	const topRail = (
		<TopBidsRail
			collectionBids={shownCollectionBids}
			groups={shownGroups}
			listings={listings}
			selectedSlug={selectedSlug}
			onSelect={setSelectedSlug}
			onPlaceBid={setBidSlug}
			biddingOpen={biddingOpen}
		/>
	)
	const activityRail = <BidActivityRail activity={activity} />

	const metrics = [
		{
			label: 'Total offers',
			sats: collectionTotal + inscriptionTotal,
			sub: exchangeRate
				? `$${Math.round(
						((collectionTotal + inscriptionTotal) / 1e8) * exchangeRate
				  ).toLocaleString('en-US')}`
				: ''
		},
		{
			label: 'Collection offers',
			sats: collectionTotal,
			sub: `${shownCollectionBids.length} price level${shownCollectionBids.length === 1 ? '' : 's'}`
		},
		{
			label: 'Inscription offers',
			sats: inscriptionTotal,
			sub: `${inscriptionCount} item${inscriptionCount === 1 ? '' : 's'}`
		},
		{ label: 'Top offer', sats: topBid?.priceSats ?? 0, sub: topBid?.collection.name ?? '' },
		{ label: 'Buyers', count: bidders, sub: 'unique addresses' }
	]

	const boardState =
		status === 'loading' && liveBids === 0 ? (
			<BoardSkeleton />
		) : status === 'error' && liveBids === 0 ? (
			<div
				role="status"
				className="flex h-40 flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border text-sm text-muted-foreground"
			>
				Offers are temporarily unavailable.
				<button type="button" onClick={retry} className="font-semibold text-foreground underline">
					Retry
				</button>
			</div>
		) : empty ? (
			<div
				data-testid="bids-empty"
				className="flex min-h-40 flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-border px-6 py-8 text-center"
			>
				<span className="text-sm font-semibold text-foreground">No funded offers yet</span>
				<span className="max-w-sm text-xs text-muted-foreground">
					An offer made on any collection or inscription page appears here the moment it is funded.
				</span>
				<Link
					href="/collect"
					className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-gradient-to-r from-purple-600 to-blue-500 px-4 text-xs font-semibold text-white transition-opacity hover:opacity-90"
				>
					Browse collections <ArrowRight className="size-3.5" aria-hidden="true" />
				</Link>
			</div>
		) : null

	const center = (
		<div className="pb-10 pt-4">
			<section
				aria-labelledby="collection-bids-heading"
				className={styles.enter}
				style={{ '--enter-delay': '80ms' } as React.CSSProperties}
			>
				<div className="mb-3 flex flex-wrap items-center justify-between gap-3">
					<div className="flex flex-wrap items-center gap-2.5">
						<h2
							id="collection-bids-heading"
							className="flex items-center gap-2 text-lg font-bold text-foreground"
						>
							<span aria-hidden="true" className="h-4 w-1 rounded-full bg-[#9E77ED]" />
							Collection Offers
						</h2>
						<BidAmount sats={collectionTotal} className="text-xs text-foreground" />
					</div>
					{shownCollectionBids.length > 0 && (
						<div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
							<span className="inline-flex items-center gap-1.5" data-testid="bids-legend">
								<span aria-hidden="true" className="flex overflow-hidden rounded-sm">
									{[72, 84, 98].map((p) => (
										<span key={p} className="h-2.5 w-3.5" style={{ backgroundColor: floorTint(p) }} />
									))}
								</span>
								Tint: closer to floor · Size: book depth
							</span>
						</div>
					)}
				</div>
				{boardState ?? (
					<ErrorBoundary>
						{shownCollectionBids.length > 0 ? (
							<CollectionBidsTreemap
								bids={shownCollectionBids}
								width={centerWidth}
								pulses={pulses}
								selectedSlug={selectedSlug}
								onSelect={desktop ? setSelectedSlug : undefined}
								onShowAll={() => {
									if (!desktop) setMobileView('top')
								}}
								entering={entering}
							/>
						) : (
							<div className="flex h-28 items-center justify-center rounded-lg border border-dashed border-border text-sm text-muted-foreground">
								No collection offers yet. Every collection page has a Make an offer tile.
							</div>
						)}
					</ErrorBoundary>
				)}
			</section>

			{shownGroups.length > 0 && (
				<section
					aria-labelledby="inscription-bids-heading"
					className={classNames('mt-10', styles.enter)}
					style={{ '--enter-delay': '200ms' } as React.CSSProperties}
				>
					<div className="mb-3 flex flex-wrap items-center gap-2.5">
						<h2
							id="inscription-bids-heading"
							className="flex items-center gap-2 text-lg font-bold text-foreground"
						>
							<span aria-hidden="true" className="h-4 w-1 rounded-full bg-[#9E77ED]" />
							Inscription Offers
						</h2>
						<BidAmount sats={inscriptionTotal} className="text-xs text-foreground" />
					</div>
					<ErrorBoundary>
						<InscriptionBidsTreemap
							groups={shownGroups}
							width={centerWidth}
							pulses={pulses}
							entering={entering}
						/>
					</ErrorBoundary>
				</section>
			)}
		</div>
	)

	return (
		<div
			ref={rootRef}
			className="flex min-h-0 flex-col"
			style={desktop ? { height: 'calc(100dvh - var(--ow-header-h) - 32px)', overflow: 'hidden' } : undefined}
			data-testid="bids-page"
			data-status={status}
		>
			{/* Stat strip, in the collection header's idiom. */}
			<div className={classNames('w-full shrink-0 max-w-[100vw] border-y border-border bg-card text-foreground', styles.enter)}>
				<div className="flex flex-col gap-3 px-5 py-3 md:px-6 md:py-3.5 xl:flex-row xl:items-center xl:gap-0">
					<div className="flex min-w-0 items-center gap-3 xl:w-[280px] xl:flex-none xl:pr-5">
						<div className="shrink-0 rounded-2xl border border-border bg-muted p-1">
							<div
								className="flex size-12 items-center justify-center rounded-xl text-white"
								style={{ background: 'linear-gradient(135deg, #B491FF, #3300FF)' }}
							>
								<BidIcon className="size-6" />
							</div>
						</div>
						<div className="min-w-0">
							<div className="flex items-center gap-2">
								<span className="text-xl font-semibold tracking-tight">Offers</span>
							</div>
							<div className="mt-1 text-[11px] text-muted-foreground" data-testid="bids-live-note">
								{status === 'loading' && liveBids === 0
									? 'Reading the book…'
									: `${liveBids.toLocaleString('en-US')} live funded offer${liveBids === 1 ? '' : 's'}`}
							</div>
						</div>
					</div>
					<div className="overflow-x-auto scrollbar-hide xl:flex xl:min-w-0 xl:flex-1 xl:self-stretch xl:border-l xl:border-border xl:pl-5">
						<div className="flex min-w-max items-stretch divide-x divide-border xl:h-full xl:min-h-[64px]">
							{metrics.map((m, index) => (
								<div
									key={m.label}
									className={`flex min-w-[118px] flex-col justify-center gap-1.5 px-4 xl:min-w-[108px] xl:px-3 2xl:min-w-[124px] 2xl:px-5 ${
										index === 0 ? 'pl-0' : ''
									}`}
								>
									<div className="text-[10px] font-semibold text-muted-foreground">
										{m.label}
									</div>
									<div className="flex min-w-0 items-center gap-1 text-[17px] font-semibold leading-none tracking-[-0.02em] text-foreground">
										{'count' in m ? (
											<span>{m.count?.toLocaleString('en-US')}</span>
										) : (
											<>
												<span className="truncate">{formatBtcTotal(m.sats ?? 0)}</span>
												<BitcoinIcon className="size-3.5 shrink-0" />
											</>
										)}
									</div>
									<div className="min-h-[12px] max-w-[160px] truncate text-[11px] leading-none text-muted-foreground">
										{m.sub || ' '}
									</div>
								</div>
							))}
						</div>
					</div>
				</div>
			</div>

			{desktop ? (
				<motion.div
					ref={gridRef}
					className="collection-drawers relative grid min-h-0 flex-1 overflow-hidden"
					style={
						{
							'--rail-width': COLLECTION_RAIL_WIDTH,
							'--trade-progress': 1,
							'--activity-progress': 1
						} as MotionStyle
					}
				>
					<div
						id="bids-trade-panel"
						className={classNames('collection-drawer collection-drawer-trade', styles.enter)}
						style={{ '--enter-delay': '140ms' } as React.CSSProperties}
					>
						<div className="drawer-content border-r border-border">
							<ErrorBoundary>{topRail}</ErrorBoundary>
						</div>
					</div>
					<div className="relative min-w-0 w-full overflow-y-auto px-4">
						<div ref={centerRef}>{center}</div>
					</div>
					<div
						id="bids-activity-panel"
						className={classNames('collection-drawer h-full', styles.enter)}
						style={{ '--enter-delay': '220ms' } as React.CSSProperties}
					>
						<div className="drawer-content flex flex-col border-l border-border">
							<ErrorBoundary>{activityRail}</ErrorBoundary>
						</div>
					</div>
				</motion.div>
			) : (
				<div className="flex flex-col">
					<div className="sticky top-[var(--ow-header-h)] z-10 grid grid-cols-3 border-b border-border bg-background">
						{(['bids', 'top', 'activity'] as const).map((v) => (
							<button
								key={v}
								type="button"
								onClick={() => setMobileView(v)}
								aria-pressed={mobileView === v}
								className={classNames(
									'h-11 border-b-2 text-xs font-medium',
									mobileView === v
										? 'border-[#9E77ED] text-foreground'
										: 'border-transparent text-muted-foreground'
								)}
							>
								{v === 'bids' ? 'Offers' : v === 'top' ? 'Top offers' : 'Activity'}
							</button>
						))}
					</div>
					<div className={classNames('px-4', mobileView !== 'bids' && 'hidden')}>
						<div ref={centerRef}>{center}</div>
					</div>
					{mobileView === 'top' && (
						<div className="h-[calc(100dvh-220px)] min-h-[420px]">{topRail}</div>
					)}
					{mobileView === 'activity' && (
						<div className="h-[calc(100dvh-220px)] min-h-[420px]">{activityRail}</div>
					)}
				</div>
			)}

			{bidCollection && (
				<MakeOfferModal
					open
					surface="bids_page"
					onClose={() => setBidSlug(null)}
					collectionSlug={bidCollection.slug}
					initialScope="collection"
					initialPriceSats={bidTop?.priceSats}
					name={bidCollection.name}
					supply={bidCollection.total_supply ?? undefined}
					collectionIcon={
						<CollectionIcon
							collection={bidCollection as any}
							className="size-10 overflow-hidden rounded-lg"
						/>
					}
					reference={{
						floorSats: bidCollection.floor_price ?? null,
						topOfferSats: bidTop?.priceSats ?? null
					}}
					onPlaced={(offer) => {
						setBidSlug(null)
						addActivity([
							{
								id: `mine-${offer.id}`,
								kind: 'placed',
								scope: 'collection',
								slug: bidCollection.slug,
								collection: bidCollection,
								name: bidCollection.name,
								priceSats: offer.price_sats,
								bidder: offer.buyer_address,
								at: Date.now()
							}
						])
					}}
				/>
			)}
		</div>
	)
}
